import * as React from 'react';
import propz from 'propz';
import * as Yup from 'yup';
import moment from 'moment';
import { useRef } from 'react';
import { Field, Form, Formik } from 'formik';
import { USER_GENDER, GENDER_SERVER_TO_CLIENT_MAPPING } from '../../consts/user';
import { Autocomplete } from '../Autocomplete/Autocomplete';
import { AppUser } from 'Src/views/App/App';
import { searchFunctionSchoolUnionSchoolForms, getName } from 'Src/helpers/autocomplete/autocomplete';
import { SchoolStudent } from 'Src/models/schoolStudent';
import { UnsavedChangesModal } from '../UnsavedChangesModal/UnsavedChangesModal';
import { DatePicker as SitDatePicker } from '../DatePicker/DatePicker';
import './SchoolUnionSchoolStudentForm.scss';

interface Props {
  student: any;
  onSubmit: (data: any, isContinue: boolean, isSavingSimilarStudentConfirmed: boolean) => Promise<boolean>;
  onCancel: () => void;
  user: AppUser;
  isCreateStudentsChain: boolean;
  identicalStudents: SchoolStudent[];
  similarStudents: SchoolStudent[];
  isFormsExist: boolean;
  isEditStudent: boolean;
  schoolId: string;
}

const StudentEditSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  form: Yup.mixed().required('Required'),
  birthday: Yup.date().nullable()
});

export interface SchoolUnionSchoolStudentFormHandle {
  requestClose: () => void;
}

export const SchoolUnionSchoolStudentForm = React.forwardRef<SchoolUnionSchoolStudentFormHandle, Props>(
  (props, ref) => {
    const {
      student,
      onSubmit,
      onCancel,
      isCreateStudentsChain,
      identicalStudents,
      similarStudents,
      isFormsExist,
      isEditStudent
    } = props;
    const isStudentExist = typeof student !== 'undefined';
    const isEditStudentMode = isEditStudent || (isStudentExist && !isCreateStudentsChain);
    const isDirtyRef = useRef(false);
    const [isDiscardWarningOpen, setIsDiscardWarningOpen] = React.useState(false);
    const isIdenticalStudentsExist = identicalStudents.length > 0;
    const isSimilarStudentsExist = similarStudents.length > 0;

    let isSaveAndContinueClick = false;

    const requestClose = React.useCallback(() => {
      if (isDirtyRef.current) {
        setIsDiscardWarningOpen(true);
        return;
      }

      onCancel();
    }, [onCancel]);

    React.useImperativeHandle(ref, () => ({ requestClose }), [requestClose]);

    const getSchoolUnionSchoolForms = (text: string, setFieldValue, schoolId: string) => {
      const user = props.user;

      if (text === '') {
        setFieldValue('formId', '');
        setFieldValue('form', undefined);
      }

      return searchFunctionSchoolUnionSchoolForms(user, schoolId, text);
    };

    const studentForm = {
      firstName: isEditStudentMode ? propz.get(student, ['firstName'], '') : '',
      lastName: isEditStudentMode ? propz.get(student, ['lastName'], '') : '',
      gender: isEditStudentMode ? propz.get(student, ['gender'], USER_GENDER.MALE) : USER_GENDER.MALE,
      birthday:
        isEditStudentMode && student.birthday && moment(student.birthday).isValid()
          ? moment(student.birthday).toDate()
          : null,
      formId: isEditStudentMode ? propz.get(student, ['formId'], '') : '',
      form: isEditStudentMode ? propz.get(student, ['form']) : undefined
    };

    const onSubmitForm = (values, isContinue, resetForm?) => {
      const dataCopy = { ...values };
      const isResetFormExist = typeof resetForm !== 'undefined';
      const isSavingSimilarStudentConfirmed = isSimilarStudentsExist && !isIdenticalStudentsExist;

      isSaveAndContinueClick = false;

      if (dataCopy.birthday) {
        dataCopy.birthday = moment(values.birthday).format('YYYY-MM-DD');
      } else {
        delete dataCopy.birthday;
      }

      return onSubmit(dataCopy, isContinue, isSavingSimilarStudentConfirmed).then(isSaved => {
        if (isSaved) {
          isDirtyRef.current = false;
          if (isResetFormExist) {
            resetForm();
          }
        }
        return isSaved;
      });
    };

    const renderSimilarStudents = () => {
      const studentToAddFirstName = propz.get(student, ['firstName'], '');
      const studentToAddLastName = propz.get(student, ['lastName'], '');
      const studentToAddBirthday = propz.get(student, ['birthday'], '');

      const similarStudentsRow = similarStudents.map(similarStudent => {
        const similarStudentFirstName = propz.get(similarStudent, ['firstName'], '');
        const similarStudentLastName = propz.get(similarStudent, ['lastName'], '');
        const similarStudentBirthday = propz.get(similarStudent, ['birthday'], '');

        return (
          <p
            key={similarStudent.id}
            className="text-primary"
          >{`${similarStudentFirstName} ${similarStudentLastName} ${similarStudentBirthday}`}</p>
        );
      });

      return (
        <div>
          <p className="text-danger">
            There seems to be a student with similar details. Please check the data to avoid duplicating students.
          </p>
          <p className="font-weight-bold mb-0">The student you are adding: </p>
          <p className="text-primary">{`${studentToAddFirstName} ${studentToAddLastName} ${studentToAddBirthday}`}</p>
          <p className="font-weight-bold mb-0">The existing student(s): </p>
          {similarStudentsRow}
        </div>
      );
    };

    return (
      <div className="suStudentForm">
        {!isFormsExist && (
          <div className="suStudentForm__notice suStudentForm__notice--danger">
            Add at least one school form (class) before creating a student.
          </div>
        )}

        {isIdenticalStudentsExist && (
          <div className="text-danger">
            <p>There is already a student in the system, matching the data you have entered.</p>
            <p>
              If you need to change any additional details like form, house, etc. please select the existing student and
              edit their data.
            </p>
          </div>
        )}

        {isSimilarStudentsExist && !isIdenticalStudentsExist && renderSimilarStudents()}

        <Formik
          initialValues={studentForm}
          validationSchema={StudentEditSchema}
          onSubmit={(values, formikActions) => {
            isSaveAndContinueClick ? onSubmitForm(values, true, formikActions.resetForm) : onSubmitForm(values, false);
          }}
        >
          {({ touched, errors, setFieldValue, values, handleSubmit, isSubmitting, dirty }) => {
            isDirtyRef.current = dirty;

            return (
              <Form className="suStudentForm__form">
                <div className="suStudentForm__grid">
                  <div className="suStudentForm__field">
                    <label htmlFor="suStudentFirstName">First name</label>
                    <Field id="suStudentFirstName" type="text" tabIndex="1" name="firstName" autoFocus />
                    {touched.firstName && errors.firstName && (
                      <div className="suStudentForm__error">{errors.firstName}</div>
                    )}
                  </div>

                  <div className="suStudentForm__field">
                    <label htmlFor="suStudentLastName">Last name</label>
                    <Field id="suStudentLastName" type="text" tabIndex="2" name="lastName" />
                    {touched.lastName && errors.lastName && (
                      <div className="suStudentForm__error">{errors.lastName}</div>
                    )}
                  </div>

                  <div className="suStudentForm__field">
                    <label htmlFor="suStudentGender">Gender</label>
                    <Field id="suStudentGender" component="select" tabIndex="3" name="gender">
                      <option value={USER_GENDER.MALE}>{GENDER_SERVER_TO_CLIENT_MAPPING.MALE}</option>
                      <option value={USER_GENDER.FEMALE}>{GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE}</option>
                    </Field>
                  </div>

                  <div className="suStudentForm__field">
                    <Field name="birthday">
                      {() => (
                        <div className="suStudentForm__datePicker">
                          <SitDatePicker
                            label="Date of birth"
                            mode="single"
                            value={values.birthday || null}
                            onChange={birthdayDate => setFieldValue('birthday', birthdayDate)}
                            format="DD-MM-YYYY"
                            placeholder="Select date of birth"
                            max={new Date()}
                            clearable={true}
                            align="right"
                          />
                        </div>
                      )}
                    </Field>
                    {touched.birthday && errors.birthday && (
                      <div className="suStudentForm__error">{String(errors.birthday)}</div>
                    )}
                  </div>

                  <div className="suStudentForm__field suStudentForm__field--wide">
                    <label>Form</label>
                    <Field name="form">
                      {({ field }) => {
                        return (
                          <Autocomplete
                            searchFunction={text => getSchoolUnionSchoolForms(text, setFieldValue, props.schoolId)}
                            getElementTitle={getName}
                            customClass="suStudentForm__autocomplete"
                            placeholder="Start typing a form name"
                            defaultItem={values.form}
                            tabIndex={5}
                            onSelect={form => {
                              setFieldValue('formId', form.id);
                              setFieldValue('form', form);
                            }}
                          />
                        );
                      }}
                    </Field>
                    {touched.form && errors.form && <div className="suStudentForm__error">{String(errors.form)}</div>}
                  </div>
                </div>

                <div className="suStudentForm__actions">
                  <button type="button" className="suStudentForm__button is-secondary" onClick={requestClose}>
                    Cancel
                  </button>
                  {!isEditStudent && (
                    <button
                      type="button"
                      className="suStudentForm__button is-secondary"
                      tabIndex={7}
                      onClick={() => {
                        isSaveAndContinueClick = true;
                        handleSubmit();
                      }}
                      disabled={!isFormsExist || isSubmitting}
                    >
                      Save &amp; add another
                    </button>
                  )}
                  <button
                    type="button"
                    className="suStudentForm__button is-primary"
                    tabIndex={8}
                    onClick={() => handleSubmit()}
                    disabled={!isFormsExist || isSubmitting}
                  >
                    Save
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
        <UnsavedChangesModal
          isOpen={isDiscardWarningOpen}
          message="This student has unsaved changes. If you close now, those changes will be lost."
          onKeepEditing={() => setIsDiscardWarningOpen(false)}
          onDiscard={() => {
            isDirtyRef.current = false;
            setIsDiscardWarningOpen(false);
            onCancel();
          }}
        />
      </div>
    );
  }
);

SchoolUnionSchoolStudentForm.displayName = 'SchoolUnionSchoolStudentForm';
